/**
 * admin「新規追加」機能の入力ルール。
 * 拡張子・上限サイズは lambda/content-api/handler.mjs 側の検証と揃えている
 * （フロントで弾けなかった場合もAPIで 400 になる）。
 */

export const VIDEO_EXTENSIONS = ['mp4', 'mov', 'm4v', 'webm', 'mkv', 'avi', 'mpg', 'mpeg', 'wmv'];
export const THUMBNAIL_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp'];
export const MATERIAL_EXTENSIONS = [
  'zip',
  '7z',
  'rar',
  'pdf',
  'psd',
  'clip',
  'jpg',
  'jpeg',
  'png',
  'webp',
  'txt',
  'csv',
];

export const MAX_VIDEO_BYTES = 5 * 1024 * 1024 * 1024;
export const MAX_THUMBNAIL_BYTES = 10 * 1024 * 1024;
export const MAX_MATERIAL_BYTES = 500 * 1024 * 1024;

export const MAX_TITLE_LENGTH = 255;
export const MAX_QUESTIONNAIRE_URL_LENGTH = 2048;

export const VIDEO_ACCEPT = VIDEO_EXTENSIONS.map((ext) => `.${ext}`).join(',');
export const THUMBNAIL_ACCEPT = THUMBNAIL_EXTENSIONS.map((ext) => `.${ext}`).join(',');
export const MATERIAL_ACCEPT = MATERIAL_EXTENSIONS.map((ext) => `.${ext}`).join(',');

export function fileExtensionOf(fileName) {
  const matched = String(fileName || '')
    .toLowerCase()
    .match(/\.([a-z0-9]{1,8})$/);
  return matched ? matched[1] : '';
}

export function formatBytes(bytes) {
  const value = Number(bytes || 0);
  if (!(value > 0)) return '-';
  const units = ['B', 'KB', 'MB', 'GB'];
  let index = 0;
  let current = value;
  while (current >= 1024 && index < units.length - 1) {
    current /= 1024;
    index += 1;
  }
  return `${current >= 10 || index === 0 ? Math.round(current) : current.toFixed(1)}${units[index]}`;
}

export function formatDurationSeconds(seconds) {
  const total = Math.floor(Number(seconds || 0));
  if (!(total > 0)) return '-';
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const rest = total % 60;
  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(rest).padStart(2, '0')}`;
  }
  return `${minutes}:${String(rest).padStart(2, '0')}`;
}

/** 動画の尺をブラウザ側で読む。読めなかった場合は変換後に MediaConvert の実測値で埋める。 */
export function readVideoDurationSeconds(file) {
  return new Promise((resolve) => {
    if (!file || typeof window === 'undefined') {
      resolve(null);
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    const video = document.createElement('video');
    let settled = false;

    const finish = (value) => {
      if (settled) return;
      settled = true;
      video.removeAttribute('src');
      video.load();
      URL.revokeObjectURL(objectUrl);
      resolve(value);
    };

    const timer = window.setTimeout(() => finish(null), 20000);

    video.preload = 'metadata';
    video.muted = true;
    video.onloadedmetadata = () => {
      window.clearTimeout(timer);
      const duration = Number(video.duration);
      finish(Number.isFinite(duration) && duration > 0 ? Math.round(duration) : null);
    };
    video.onerror = () => {
      window.clearTimeout(timer);
      finish(null);
    };
    video.src = objectUrl;
  });
}

/**
 * アンケートURLの形式チェック。`videos.questionnaire_url` は再生ページでそのまま
 * `<a href>` に載せるため、http / https 以外のスキームは受け付けない。
 */
export function isHttpUrl(value) {
  let parsed;
  try {
    parsed = new URL(String(value));
  } catch {
    return false;
  }
  return parsed.protocol === 'http:' || parsed.protocol === 'https:';
}

function validateFile(file, { extensions, maxBytes, label, required }) {
  if (!file) return required ? `${label}を選択してください` : null;

  const extension = fileExtensionOf(file.name);
  if (!extension || !extensions.includes(extension)) {
    return `${label}の形式が対応していません（${extensions.join(' / ')}）`;
  }
  if (file.size > maxBytes) {
    return `${label}のサイズが上限（${formatBytes(maxBytes)}）を超えています`;
  }
  return null;
}

/**
 * 配布資料の検証。新規追加フォームと、一覧からの差し替えの両方で使う。
 * 問題なければ `null` を返す。
 */
export function validateMaterialFile(file, { required = false } = {}) {
  return validateFile(file, {
    extensions: MATERIAL_EXTENSIONS,
    maxBytes: MAX_MATERIAL_BYTES,
    label: '配布資料',
    required,
  });
}

/**
 * アンケートURLの検証。新規追加・変更のどちらも必須で、`http(s)` のみ受け付ける。
 * 問題なければ `null` を返す。
 */
export function validateQuestionnaireUrl(rawUrl) {
  const questionnaireUrl = String(rawUrl ?? '').trim();

  if (!questionnaireUrl) return 'アンケートURLを入力してください';
  if (questionnaireUrl.length > MAX_QUESTIONNAIRE_URL_LENGTH) {
    return `アンケートURLは${MAX_QUESTIONNAIRE_URL_LENGTH}文字以内で入力してください`;
  }
  if (!isHttpUrl(questionnaireUrl)) {
    return 'アンケートURLは http:// または https:// で始まるURLを入力してください';
  }
  return null;
}

/** 入力フォームの検証。キーは form のフィールド名に合わせる。 */
export function validateAdminVideoForm(form) {
  const errors = {};

  const videoError = validateFile(form.videoFile, {
    extensions: VIDEO_EXTENSIONS,
    maxBytes: MAX_VIDEO_BYTES,
    label: '動画ファイル',
    required: true,
  });
  if (videoError) errors.videoFile = videoError;

  const thumbnailError = validateFile(form.thumbnailFile, {
    extensions: THUMBNAIL_EXTENSIONS,
    maxBytes: MAX_THUMBNAIL_BYTES,
    label: 'サムネイル画像',
    required: true,
  });
  if (thumbnailError) errors.thumbnailFile = thumbnailError;

  const materialError = validateMaterialFile(form.materialFile);
  if (materialError) errors.materialFile = materialError;

  const title = String(form.title ?? '').trim();
  if (!title) {
    errors.title = '動画タイトルを入力してください';
  } else if (title.length > MAX_TITLE_LENGTH) {
    errors.title = `動画タイトルは${MAX_TITLE_LENGTH}文字以内で入力してください`;
  }

  if (!form.categoryId) {
    errors.categoryId = 'カテゴリを選択してください';
  }

  const questionnaireUrlError = validateQuestionnaireUrl(form.questionnaireUrl);
  if (questionnaireUrlError) errors.questionnaireUrl = questionnaireUrlError;

  return errors;
}
